import clsx from "clsx";
import { Fragment, useState } from "react";
import { MOOD_TRAITS } from "../../../../common/constants.ts";
import i18n from "../util/i18n.ts";
import type {
	GameAttributesLeague,
	MoodComponents,
	MoodTrait,
} from "../../../../common/types.ts";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { pronounLocalized } from "../util/pronounLocalized.ts";
import { ResponsivePopover } from "./ResponsivePopover.tsx";
import { useLocalPartial } from "../util/local.ts";
import {
	formatPlayerName,
	formatPlayerNameFromLegacyName,
} from "../util/playerNames.ts";
import { translateTeamShortName } from "../util/teamNames.ts";
import PageTabs from "./PageTabs.tsx";

const componentText = (
	component: keyof MoodComponents,
	value: number,
	gender: GameAttributesLeague["gender"],
) => {
	if (value === 0) {
		return;
	}

	const his = pronounLocalized(gender, "his");
	const he = pronounLocalized(gender, "he");

	if (value > 0) {
		switch (component) {
			case "marketSize":
				return i18n.t("mood.marketSizeLarge", { ns: "ui", defaultValue: "Enjoys playing in a large market" });
			case "facilities":
				return i18n.t("mood.facilitiesGood", { ns: "ui", defaultValue: "Likes the lavish team facilities" });
			case "teamPerformance":
				return i18n.t("mood.teamPerformanceGood", { ns: "ui", defaultValue: "Happy with the team's performance" });
			case "hype":
				return i18n.t("mood.hypeGood", { ns: "ui", defaultValue: "Likes the energy from the fan base" });
			case "loyalty":
				return i18n.t("mood.loyalty", { ns: "ui", defaultValue: "Is loyal to the franchise" });
			case "trades":
				throw new Error("Should never happen");
			case "playingTime":
				return i18n.t("mood.playingTimeGood", { ns: "ui", defaultValue: `Happy with ${his} playing time`, his });
			case "rookieContract":
				return i18n.t("mood.rookieContract", { ns: "ui", defaultValue: "Eager to sign first non-rookie contract" });
			case "relatives":
				return i18n.t("mood.relatives", {
					ns: "ui",
					defaultValue: `Wants to play with ${his} ${helpers.plural("relative", value / 2)}`,
					his,
					relative: helpers.plural("relative", value / 2),
				});
		}
	}

	switch (component) {
		case "marketSize":
			return i18n.t("mood.marketSizeSmall", { ns: "ui", defaultValue: "Dislikes playing in a small market" });
		case "facilities":
			return i18n.t("mood.facilitiesBad", { ns: "ui", defaultValue: "Dislikes the outdated team facilities" });
		case "teamPerformance":
			return i18n.t("mood.teamPerformanceBad", { ns: "ui", defaultValue: "Unhappy with the team's performance" });
		case "hype":
			return i18n.t("mood.hypeBad", { ns: "ui", defaultValue: "Wishes fans were more excited" });
		case "loyalty":
			throw new Error("Should never happen");
		case "trades":
			return i18n.t("mood.tradeWorry", { ns: "ui", defaultValue: `Worried ${he}'ll be traded away`, he });
		case "playingTime":
			return i18n.t("mood.playingTimeBad", { ns: "ui", defaultValue: "Wants more playing time" });
		case "rookieContract":
			throw new Error("Should never happen");
		case "relatives":
			throw new Error("Should never happen");
	}
};

const highlightColor = (sum: number) =>
	clsx({
		"text-danger": sum < 0,
		"text-success": sum > 0,
		"text-body-secondary": sum === 0,
	});

const plusMinus = (sum: number) => `${sum > 0 ? "+" : ""}${sum}`;

const plusMinusStyle = {
	minWidth: 14,
};

const getMoodTraitLabel = (trait: MoodTrait) =>
	i18n.t(`moodTraitsShort.${trait}`, {
		ns: "ui",
		defaultValue: trait,
	});

const roundProbWilling = (probWilling: number) => {
	if (probWilling < 1 && probWilling > 0.99) {
		return ">99";
	}
	if (probWilling > 0 && probWilling < 0.01) {
		return "<1";
	}

	return Math.round(probWilling * 100);
};

export const processComponents = (components: MoodComponents) => {
	const componentsRounded = {
		...components,
	};
	let sum = 0;
	for (const key of helpers.keys(componentsRounded)) {
		if (key === "custom") {
			for (const row of componentsRounded.custom!) {
				sum += row.amount;
			}
		} else {
			componentsRounded[key] = Math.round(componentsRounded[key]);
			sum += componentsRounded[key];
		}
	}

	return {
		componentsRounded,
		sum,
	};
};

type PlayerMood = {
	components: MoodComponents;
	probWilling: number;
	traits: MoodTrait[];
};

export type MoodHost =
	| "player-profile"
	| "free-agents"
	| "negotiation-list"
	| "roster"
	| "negotiation-modal";

export const resolveMoodType = (
	requested: "user" | "current",
	canShowCurrent: boolean,
) => (requested === "current" && canShowCurrent ? "current" : "user");

type Props = {
	className?: string;
	host: MoodHost;
	maxWidth?: boolean;
	playerHref?: string;
	p: {
		pid: number;
		name?: string;
		firstName?: string;
		firstNameShort?: string;
		lastName?: string;
		mood: {
			user: PlayerMood;
			current?: PlayerMood;
		};
		tid: number;
	};
	defaultType: "user" | "current";
};

const MoodTextRow = ({ amount, text }: { amount: number; text: string }) => {
	return (
		<tr className={highlightColor(amount)} data-mood-component-amount={amount}>
			<td className="text-end p-0">{plusMinus(amount)}</td>
			<td className="p-0 ps-1">{text}</td>
		</tr>
	);
};

export const Mood = ({ className, defaultType, host, maxWidth, p, playerHref }: Props) => {
	const { teamInfoCache, userTid } = useLocalPartial([
		"teamInfoCache",
		"userTid",
	]);

	const playerIsOnUsersTeam = userTid === p.tid;
	const canShowCurrent = Boolean(p.mood.current && !playerIsOnUsersTeam);
	const initialType = resolveMoodType(defaultType, canShowCurrent);
	const scopeKey = [
		p.pid,
		p.tid,
		userTid,
		defaultType,
		canShowCurrent ? "current-available" : "user-only",
	].join(":");
	const [selection, setSelection] = useState<{
		scopeKey: string;
		type: "user" | "current";
	}>({ scopeKey, type: initialType });
	const type =
		selection.scopeKey === scopeKey
			? resolveMoodType(selection.type, canShowCurrent)
			: initialType;

	const { gender } = useLocalPartial(["gender"]);

	const mood = p.mood[type];
	const initialMood = p.mood[initialType];

	if (!initialMood) {
		return null;
	}

	const showProbWilling = p.tid >= 0;

	const id = `mood-popover-${p.pid}`;

	let signText;
	if (type === "user") {
		if (p.tid === userTid) {
			signText = i18n.t("mood.signReSign", { ns: "ui", defaultValue: "re-sign with you" });
		} else {
			signText = i18n.t("mood.signNew", { ns: "ui", defaultValue: "sign with you" });
		}
	} else {
		const his = pronounLocalized(gender, "his");
		signText = i18n.t("mood.signCurrentTeam", { ns: "ui", defaultValue: `re-sign with ${his} current team`, his });
	}

	const playerName =
		p.name !== undefined
			? formatPlayerNameFromLegacyName(p.name)
			: p.firstName !== undefined && p.lastName !== undefined
				? formatPlayerName(p.firstName, p.lastName, p.firstNameShort)
				: "";

	const modalHeader = (
		<a href={playerHref ?? createMobileCrossTabHref(helpers.leagueUrl(["player", p.pid]))}>
			{playerName}
		</a>
	);
	let modalBody = null;
	if (mood) {
		const { componentsRounded } = processComponents(mood.components);
		const roundedProbWilling = roundProbWilling(mood.probWilling);

		modalBody = (
			<div
				data-mood-active-type={type}
				data-mood-can-show-current={canShowCurrent ? "true" : "false"}
				data-mood-default-type={defaultType}
				data-mood-host={host}
				data-mood-modal-pid={p.pid}
			>
				{mood && mood.traits.length > 0 ? (
					<p className="mb-2">
						{i18n.t("mood.priorities", { ns: "ui", defaultValue: "Priorities:" })}{" "}
						{mood.traits
							.map((trait) =>
								i18n
									.t(`moodTraits.${trait}`, {
										ns: "ui",
										defaultValue: MOOD_TRAITS[trait],
									})
									.toLowerCase(),
							)
							.join(", ")}
					</p>
				) : null}
				<PageTabs
					ariaLabel={i18n.t("roster.playerMood", {
						ns: "ui",
						defaultValue: "Player mood",
					})}
					className="mood-page-tabs mb-2"
					tabs={[
						{
							active: type === "user",
							key: "user",
							label: translateTeamShortName(teamInfoCache[userTid]),
							onClick: () => {
								setSelection({ scopeKey, type: "user" });
							},
						},
						...(canShowCurrent
							? [
									{
										active: type === "current",
										key: "current",
										label: translateTeamShortName(teamInfoCache[p.tid]),
										onClick: () => {
											setSelection({ scopeKey, type: "current" });
										},
									},
								]
							: []),
					]}
				/>
				<table>
					<tbody>
						{helpers.keys(componentsRounded).map((key) => {
							if (key === "custom") {
								return (
									<Fragment key={key}>
										{componentsRounded.custom!.map((row, i) => {
											if (row.amount === 0) {
												return null;
											}

											return (
												<MoodTextRow
													key={i}
													amount={row.amount}
													text={row.text}
												/>
											);
										})}
									</Fragment>
								);
							}

							const text = componentText(key, componentsRounded[key], gender);
							if (!text) {
								return null;
							}

							return (
								<MoodTextRow
									key={key}
									amount={componentsRounded[key]}
									text={text}
								/>
							);
						})}
					</tbody>
				</table>
				{showProbWilling ? (
					<p className="mt-1 mb-0">
						{i18n.t("mood.odds", { ns: "ui", defaultValue: "Odds player would {{signText}}: {{prob}}%", signText, prob: roundedProbWilling })}
					</p>
				) : null}
			</div>
		);
	}

	const popoverContent = (
		<div
			style={{
				minWidth: 250,
			}}
		>
			<p className="mb-2">{modalHeader}</p>
			{modalBody}
		</div>
	);

	const { sum } = processComponents(initialMood.components);
	const roundedProbWilling = roundProbWilling(initialMood.probWilling);
	const renderTarget = ({ onClick }: { onClick?: () => void }) => {
		return (
			<button
				className={clsx(
					"btn btn-xs d-flex mood-trigger",
					className,
					initialType === "user"
						? "btn-light-bordered-primary"
						: "btn-light-bordered",
					{
						"w-100": maxWidth,
					},
				)}
				data-mood-active-type={type}
				data-mood-can-show-current={canShowCurrent ? "true" : "false"}
				data-mood-default-type={defaultType}
				data-mood-trigger-pid={p.pid}
				data-mood-host={host}
				onClick={onClick}
				type="button"
			>
				<span
					className={`text-end ${highlightColor(sum)}`}
					data-no-row-highlight="true"
					style={plusMinusStyle}
				>
					{plusMinus(sum)}
				</span>
				<div className="ms-1 me-auto" data-no-row-highlight="true">
					{initialMood.traits.map(getMoodTraitLabel).join(" ")}
				</div>
				{showProbWilling ? (
					<span
						className="text-body-secondary ms-1"
						data-no-row-highlight="true"
					>
						{roundedProbWilling}%
					</span>
				) : null}
			</button>
		);
	};

	return (
		<ResponsivePopover
			id={id}
			modalHeader={modalHeader}
			modalBody={modalBody}
			popoverContent={popoverContent}
			renderTarget={renderTarget}
		/>
	);
};

export const wrappedMood = (props: Props) => {
	const { defaultType, p } = props;

	if (!p.mood) {
		return null;
	}

	const mood = p.mood[defaultType];

	return {
		value: <Mood {...props} />,
		sortValue: mood ? processComponents(mood.components).sum : null,
		searchValue: mood ? mood.traits.join("") : null,
	};
};
